import * as React from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';

import { Icon } from './Icon';
import { cn } from './cn';
import { useUiPortalContainer } from '@/js/wisemetering/ui/render_with_ui_root';
import { WM_FLOATING_SURFACE_CLASSNAME } from './surfaceStyles';

// DropdownMenu — click-anchored menu with structured items.
//
// Closed shape:
//
//   <DropdownMenu items={[
//       { label: 'Edit', onSelect: () => ... },
//       { label: 'Duplicate', icon: 'duplicate', onSelect: () => ... },
//       { type: 'submenu', label: 'Export as', items: [
//           { label: 'CSV', onSelect: () => ... },
//           { label: 'PDF', onSelect: () => ... }
//       ] },
//       { type: 'separator' },
//       { type: 'header', label: 'Danger zone' },
//       { label: 'Delete', icon: 'delete', tone: 'destructive', onSelect: () => ... }
//   ]}>
//       <IconButton icon="ellipsis"/>
//   </DropdownMenu>
//
// `children` is the trigger element rendered with `asChild` — same shape
// as Tooltip. Putting the trigger as children (and `items` as a config
// prop) keeps the abstractions distinct: items is data, trigger is the
// visible element you see before clicking. The JSX nesting reads
// naturally as "this Button, with these items behind it".
//
// Item shape (`type` defaults to `'item'`):
//   { label, onSelect, icon?, tone?, disabled? }   // 'item'
//   { type: 'separator' }
//   { type: 'header', label }                      // non-clickable section heading
//   { type: 'submenu', label, items }              // recursive
//
// `icon` is the WM icon name (same vocabulary as Button / IconButton) and
// renders on the right of the label, after `tw-ml-auto`. Submenu items
// reserve the right slot for the chevron — they don't accept `icon`.
//
// `modal` is forced to `false`: kebab / action menus shouldn't trap focus
// or block clicks outside. If a modal use case appears, lift it.

// Menu chrome — bakes typography on the items / headers because the
// flex+gap layout (label + trailing icon) depends on those elements
// being direct children of the row. Wrapping in <Text> would collapse
// the gap. Inline recipes — kept in sync with Typography variants of
// the same name (`control`, `caption`-like overline).
const CONTROL_TEXT_CLASSNAME = 'tw-text-sm tw-font-medium tw-leading-5';
const OVERLINE_TEXT_CLASSNAME = 'tw-text-caption tw-font-semibold tw-uppercase tw-tracking-wide tw-leading-5';

const MENU_CONTENT_CLASSNAME = `${WM_FLOATING_SURFACE_CLASSNAME} tw-z-overlay tw-min-w-[14rem] tw-overflow-hidden tw-rounded-2xl tw-p-1.5 tw-font-sans tw-text-foreground data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=open]:tw-fade-in-0 data-[state=closed]:tw-zoom-out-95 data-[state=open]:tw-zoom-in-95 data-[side=bottom]:tw-slide-in-from-top-2 data-[side=left]:tw-slide-in-from-right-2 data-[side=right]:tw-slide-in-from-left-2 data-[side=top]:tw-slide-in-from-bottom-2`;
const MENU_ITEM_CLASSNAME = `${CONTROL_TEXT_CLASSNAME} tw-relative tw-flex tw-cursor-default tw-select-none tw-items-center tw-gap-2 tw-rounded-xl tw-px-3 tw-py-2.5 tw-text-foreground tw-outline-none tw-transition-colors focus:tw-bg-muted focus:tw-text-foreground data-[disabled]:tw-pointer-events-none data-[disabled]:tw-opacity-50 [&_svg]:tw-pointer-events-none [&_svg]:tw-shrink-0`;
const MENU_HEADER_CLASSNAME = `${OVERLINE_TEXT_CLASSNAME} tw-px-3 tw-py-2 tw-text-muted-foreground`;
const MENU_SEPARATOR_CLASSNAME = 'tw-my-1.5 tw-h-px tw-bg-border';
const DESTRUCTIVE_ITEM_CLASSNAME = 'tw-text-destructive focus:tw-text-destructive';
const SUB_TRIGGER_OPEN_CLASSNAME = 'data-[state=open]:tw-bg-muted data-[state=open]:tw-text-foreground';

const TrailingIcon = ({ icon }) => (
    <span className="tw-ml-auto">
        <Icon icon={icon} size="small" tone="muted" />
    </span>
);

const renderItem = (item, index, portalContainer) => {
    const itemType = item.type ?? 'item';

    if (itemType === 'separator') {
        return (
            <DropdownMenuPrimitive.Separator
                key={index}
                className={MENU_SEPARATOR_CLASSNAME}
            />
        );
    }

    if (itemType === 'header') {
        return (
            <DropdownMenuPrimitive.Label key={index} className={MENU_HEADER_CLASSNAME}>
                {item.label}
            </DropdownMenuPrimitive.Label>
        );
    }

    if (itemType === 'submenu') {
        return (
            <DropdownMenuPrimitive.Sub key={index}>
                <DropdownMenuPrimitive.SubTrigger
                    className={cn(MENU_ITEM_CLASSNAME, SUB_TRIGGER_OPEN_CLASSNAME)}
                >
                    <span>{item.label}</span>
                    <TrailingIcon icon="chevronRight" />
                </DropdownMenuPrimitive.SubTrigger>
                <DropdownMenuPrimitive.Portal container={portalContainer ?? undefined}>
                    <DropdownMenuPrimitive.SubContent
                        className={MENU_CONTENT_CLASSNAME}
                        sideOffset={8}
                    >
                        {item.items.map((child, i) => renderItem(child, i, portalContainer))}
                    </DropdownMenuPrimitive.SubContent>
                </DropdownMenuPrimitive.Portal>
            </DropdownMenuPrimitive.Sub>
        );
    }

    // 'item' (default)
    return (
        <DropdownMenuPrimitive.Item
            key={index}
            className={cn(
                MENU_ITEM_CLASSNAME,
                item.tone === 'destructive' && DESTRUCTIVE_ITEM_CLASSNAME
            )}
            disabled={item.disabled}
            onSelect={item.onSelect}
        >
            <span>{item.label}</span>
            {item.icon ? <TrailingIcon icon={item.icon} /> : null}
        </DropdownMenuPrimitive.Item>
    );
};

function DropdownMenu({
    children,
    items,
    align = 'center',
    side = 'bottom',
    sideOffset = 8
}) {
    const portalContainer = useUiPortalContainer();

    return (
        <DropdownMenuPrimitive.Root modal={false}>
            <DropdownMenuPrimitive.Trigger asChild>{children}</DropdownMenuPrimitive.Trigger>
            <DropdownMenuPrimitive.Portal container={portalContainer ?? undefined}>
                <DropdownMenuPrimitive.Content
                    align={align}
                    side={side}
                    sideOffset={sideOffset}
                    className={MENU_CONTENT_CLASSNAME}
                >
                    {items.map((item, index) => renderItem(item, index, portalContainer))}
                </DropdownMenuPrimitive.Content>
            </DropdownMenuPrimitive.Portal>
        </DropdownMenuPrimitive.Root>
    );
}

export { DropdownMenu };
